import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { UseFileLoaderResult, UseFileInputResult } from "./FilePreviewer.types";
import { TIMING } from "./FilePreviewer.config";
import { extractFilename } from "./FilePreviewer.utils";

/**
 * Hook to load files using axios and convert them to blob URLs
 * Solves HTTP 206 partial content issues with react-file-viewer
 */
export const useFileLoader = (
  fileUrl: string,
  fileType: string,
  fileObject?: File | null
): UseFileLoaderResult => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    blobUrlRef.current = blobUrl;
  }, [blobUrl]);

  const loadFile = useCallback(async () => {
    // If we have a File object, prioritize it over the URL
    if (fileObject) {
      setLoading(true);
      setError(null);

      try {
        // Create fresh blob URL from File object
        const newBlobUrl = URL.createObjectURL(fileObject);

        // Store the previous blob URL for delayed cleanup
        const prevBlobUrl = blobUrlRef.current;

        // Set the new blob URL immediately
        setBlobUrl(newBlobUrl);

        // Clean up previous blob url
        if (prevBlobUrl) {
          setTimeout(() => {
            URL.revokeObjectURL(prevBlobUrl);
          }, TIMING.BLOB_CLEANUP_DELAY_MS);
        }

        setLoading(false);
        return;
      } catch (err) {
        console.error("Failed to create blob URL from file:", err);
        setError(
          err instanceof Error ? err : new Error("Failed to process file")
        );
        setLoading(false);
        return;
      }
    }

    // Fall back to existing URL-based logic
    if (!fileUrl) {
      setBlobUrl(prevBlobUrl => {
        if (prevBlobUrl) {
          setTimeout(() => {
            URL.revokeObjectURL(prevBlobUrl);
          }, TIMING.BLOB_CLEANUP_DELAY_MS);
        }
        return null;
      });
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if the fileUrl is already a blob URL (from file upload)
      if (fileUrl.startsWith("blob:")) {
        // For blob URLs, use them directly but clean up previous ones with delay
        setBlobUrl(prevBlobUrl => {
          if (prevBlobUrl && prevBlobUrl !== fileUrl) {
            setTimeout(() => {
              URL.revokeObjectURL(prevBlobUrl);
            }, TIMING.BLOB_CLEANUP_DELAY_MS);
          }
          return fileUrl;
        });
        setLoading(false);
        return;
      }

      // For regular URLs, fetch via axios
      const response = await axios.get(fileUrl, {
        responseType: "blob",
        timeout: 30000, // 30 second timeout
      });

      // Create blob URL from the response
      const blob = new Blob([response.data]);
      const newBlobUrl = URL.createObjectURL(blob);

      // Clean up previous blob URL after creating the new one with delay
      setBlobUrl(prevBlobUrl => {
        if (prevBlobUrl && prevBlobUrl !== fileUrl) {
          setTimeout(() => {
            URL.revokeObjectURL(prevBlobUrl);
          }, 100);
        }
        return newBlobUrl;
      });
    } catch (err) {
      console.error("Failed to load file:", fileUrl, err);
      setError(err instanceof Error ? err : new Error("Failed to load file"));
    } finally {
      setLoading(false);
    }
  }, [fileUrl, fileObject]);

  const fileName = fileUrl ? extractFilename(fileUrl) : undefined;

  const retry = useCallback(() => {
    loadFile();
  }, [loadFile]);

  // Load file when URL, type, or file object changes
  useEffect(() => {
    loadFile();
  }, [fileUrl, fileType, fileObject, loadFile]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);
  return { blobUrl, loading, error, retry, fileName };
};

/**
 * Hook to manage fullscreen state and behavior
 * Handles escape key listener and body overflow management
 */
export function useFullscreen(): [boolean, () => void, () => void] {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const openFullscreen = useCallback(() => setIsFullscreen(true), []);
  const closeFullscreen = useCallback(() => setIsFullscreen(false), []);
  const toggleFullscreen = useCallback(
    () => setIsFullscreen(prev => !prev),
    []
  );

  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeFullscreen();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, closeFullscreen]);

  return [isFullscreen, toggleFullscreen, closeFullscreen];
}

/**
 * Hook to manage file input functionality
 * Handles file selection, upload callback, and input reset
 */
export const useFileInput = (
  onFileUpload?: (file: File) => void,
  acceptedFileTypes?: string
): UseFileInputResult => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles file upload button click by triggering the hidden file input
   */
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handles file selection from the file input
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onFileUpload) {
      onFileUpload(file);
      // Reset the input so the same file can be selected again if needed
      event.target.value = "";
    }
  };

  return {
    fileInputRef,
    handleUploadClick,
    handleFileChange,
  };
};
